import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EditableFieldsContext,
  EditableFieldsContextState,
  INITIAL_EDITABLE_FIELDS_CONTEXT_STATE,
} from 'modules/templateEditor/context/EditableFieldsContext';
import {
  TemplateAndContentFormDataLocale,
  UpdateMediumFieldsType,
  getTemplateHasContent,
} from 'modules/templateEditor/common';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { useVariantConfigurationContext } from 'modules/experiments/contexts';

/**
 * The experiment module just needs the context attributes that are responsible
 * for populating the template preview component and the ones needed
 * to update the same.
 * The experiment module updates the template via the VariantUpdate mutation
 * which are handled inside the experiment module itself.
 */
const defaultTemplate = {} as TemplateAndContentFormDataLocale;
export const EditTemplatePreviewContextProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { isLoading, medium, template } = useTemplateQueryContext();
  const [hasContent, setHasContent] = useState<boolean>(false);
  const liveTemplateRef = useRef<TemplateAndContentFormDataLocale>(defaultTemplate);
  const [liveTemplate, setLiveTemplate] =
    useState<TemplateAndContentFormDataLocale>(defaultTemplate);
  const { selectedVariant } = useVariantConfigurationContext();

  useEffect(() => {
    if (!isLoading) {
      const currentTemplate = JSON.parse(template) as TemplateAndContentFormDataLocale;
      liveTemplateRef.current = currentTemplate;
      setLiveTemplate(currentTemplate);
      setHasContent(getTemplateHasContent(medium, currentTemplate?.mediumData || {}));
    }
  }, [isLoading, medium]);

  const setCurrentMediumFields = useCallback(
    (updatedFields: UpdateMediumFieldsType) => {
      const { mediumData = {}, ...commonFields } = liveTemplateRef.current;
      const newMediumData = {
        ...mediumData,
        ...updatedFields,
      };
      liveTemplateRef.current = { ...liveTemplateRef.current, mediumData: newMediumData };
      setLiveTemplate({
        ...commonFields,
        mediumData: newMediumData,
      });
      setHasContent(getTemplateHasContent(medium, newMediumData));
    },
    [medium],
  );

  /**
   * Responsible for binding data between the template
   * preview component and the selected variant inside
   * the experiment setup module.
   */
  useEffect(() => {
    if (selectedVariant?.content) {
      setCurrentMediumFields({
        ...selectedVariant.content,
      });
    }
  }, [selectedVariant?.content]);

  const value: EditableFieldsContextState = {
    ...INITIAL_EDITABLE_FIELDS_CONTEXT_STATE,
    hasContent,
    liveTemplate,
  };

  return <EditableFieldsContext.Provider value={value}>{children}</EditableFieldsContext.Provider>;
};
